/**
 * Team Dashboard data hook for AI Recruit
 * Fetches team-level analytics aggregated across all managed team members.
 */

import { useState, useEffect } from 'react'
import { analyticsApi } from '../services/api'
import type {
  DateRangeOption,
  TrendData,
  StatData,
  StatusBreakdownItem,
  JobSubmissionData,
  SourceBreakdownItem,
} from './useDashboard'

export interface MemberStatItem {
  user_id: string
  full_name: string
  email: string
  role: string
  resumes_uploaded: number
  jobs_assigned: number
  matches_applied: number
}

export interface MemberJobItem {
  job_uuid: string
  job_id: string | null
  job_title: string
  client_name: string
  submission_count: number
  created_by_name?: string | null
  created_at?: string | null
  assigned_recruiters?: string[]
}

export interface MemberJobBreakdownItem {
  user_id: string
  full_name: string
  role: string
  jobs: MemberJobItem[]
  total_submissions: number
}

export interface TeamDashboardData {
  team_name: string
  stats: {
    resumes_uploaded: StatData
    jobs_created: StatData
    matches_applied: StatData
  }
  status_breakdown: StatusBreakdownItem[]
  job_submissions: JobSubmissionData[]
  member_stats: MemberStatItem[]
  member_job_breakdown: MemberJobBreakdownItem[]
  resume_sources: SourceBreakdownItem[]
  job_sources: SourceBreakdownItem[]
  is_empty: boolean
}

export interface UseTeamDashboardParams {
  dateRange: DateRangeOption
  customStartDate?: Date
  customEndDate?: Date
}

export interface UseTeamDashboardReturn {
  data: TeamDashboardData | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// Re-export for convenience
export type { DateRangeOption, TrendData, StatData, StatusBreakdownItem, JobSubmissionData, SourceBreakdownItem }

export const useTeamDashboard = (params: UseTeamDashboardParams): UseTeamDashboardReturn => {
  const [data, setData] = useState<TeamDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Detect user's timezone from browser
      let userTimezone = 'UTC'
      try {
        userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch (e) {
        console.warn('Failed to detect timezone, using UTC:', e)
      }

      const queryParams = new URLSearchParams({ 
        date_range: params.dateRange,
        timezone: userTimezone
      })
      
      if (params.dateRange === 'custom' && params.customStartDate && params.customEndDate) {
        queryParams.append('custom_start_date', params.customStartDate.toISOString())
        queryParams.append('custom_end_date', params.customEndDate.toISOString())
      }

      const response = await analyticsApi.getTeamDashboardStats(queryParams.toString())
      setData(response.data)
    } catch (err: any) {
      console.error('Failed to fetch team dashboard data:', err)
      setError(err.response?.data?.detail || 'Failed to load team dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const refetch = async () => {
    await fetchData()
  }

  useEffect(() => {
    fetchData()
  }, [params.dateRange, params.customStartDate, params.customEndDate])

  return { data, loading, error, refetch }
}
